import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { AlsoEnergyHardware, AlsoEnergySite } from '/server/integrations/alsoEnergy';

export interface AlsoEnergyMeter extends Doc, Omit<AlsoEnergyHardware, 'name'> {
  _id: string;
  meterId: string;
  credentialId: string;
  site: AlsoEnergySite;
}
export type Model = AlsoEnergyMeter;

export const AlsoEnergyMeters = new Mongo.Collection<AlsoEnergyMeter>('alsoEnergyMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: AlsoEnergyMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<AlsoEnergyMeter>, ListMethodResponse<AlsoEnergyMeter, Refs>>('listAlsoEnergyMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<AlsoEnergyMeter, Refs>>('getAlsoEnergyMeter'),
  createMethod: DeclareMethod<Partial<AlsoEnergyMeter>, string>('createAlsoEnergyMeter'),
  updateMethod: DeclareMethod<Partial<AlsoEnergyMeter>, string>('updateAlsoEnergyMeter'),
  deleteMethod: DeclareMethod<string>('deleteAlsoEnergyMeter'),
  pathPrefix: '/alsoenergy-meters',
  asString: (document) => document.name || document.apiKey || document._id,
  modelName: 'AlsoEnergy',
  pluralName: 'AlsoEnergies',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<AlsoEnergyMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

export const pullAlsoEnergyMeterData = DeclareMethod<{ _id: string, days?: number }>('pullAlsoEnergyMeterData');

Meteor.isServer && Meteor.startup(async () => {
  await AlsoEnergyMeters.createIndexAsync({ meterId: 1 });
});

export interface AlsoEnergyCredential extends Doc {
  _id: string;
  orgId: string;
  username: string;
  password: string;
}

export const AlsoEnergyCredentials = new Mongo.Collection<AlsoEnergyCredential>('alsoEnergyCredentials');

export const createAlsoEnergyCredential = DeclareMethod<Partial<AlsoEnergyCredential>, string>('createAlsoEnergyCredential');
export const updateAlsoEnergyCredential = DeclareMethod<Partial<AlsoEnergyCredential>>('updateAlsoEnergyCredential');
export const getAlsoEnergyCredentialsForOrg = DeclareMethod<string, AlsoEnergyCredential[]>('getAlsoEnergyCredentialsForOrg');
export const pullAlsoEnergySites = DeclareMethod<string, { numSystems: number; numCreated: number; }>('pullAlsoEnergySites');
// export const importAlsoEnergySitesData = DeclareMethod<string, void>('importAlsoEnergySitesData');

/**
 * Validates an AlsoEnergy credential's required fields.
 * @param credential - Partial credential to check.
 * @returns Field-keyed map of validation error messages.
 */
export const validateAlsoEnergyCredential = (credential: Partial<AlsoEnergyCredential>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, credential, 'orgId', { required: true });
  validateString(errors, credential, 'username', { required: true });
  validateString(errors, credential, 'password', { required: true });
  return errors;
};

/**
 * Strips a credential down to its persistable fields.
 * @param credentials - Source credential object.
 * @returns Credential with only orgId, username, and password.
 */
export const cleanAlsoEnergyCredentials = (credentials: Partial<AlsoEnergyCredential>) => pluck(credentials, ['orgId', 'username', 'password']) as AlsoEnergyCredential;